import type { StoreCapability } from '@avrahami/contracts';

export type NavKey =
  | 'dashboard'
  | 'orders'
  | 'customers'
  | 'products'
  | 'categories'
  | 'popups'
  | 'branding'
  | 'home'
  | 'pages'
  | 'settings'
  | 'analytics'
  | 'staff'
  | 'activity'
  | 'support'
  | 'account';

export type NavGroup = 'requests' | 'catalog' | 'store' | 'team';

export interface NavItem {
  key: NavKey;
  to: string;
  group: NavGroup;
  /** Every capability the screen needs; the item is hidden without them. */
  needs: readonly StoreCapability[];
}

/** The side menu, in order. Screens a role cannot use are not offered at all. */
export const NAV_ITEMS: readonly NavItem[] = [
  {
    key: 'dashboard',
    to: '/',
    group: 'requests',
    needs: ['store.settings.read', 'store.orders.read'],
  },
  // Quote requests: the code keeps the name of the API's resource (adr/0022).
  { key: 'orders', to: '/quote-requests', group: 'requests', needs: ['store.orders.read'] },
  { key: 'customers', to: '/customers', group: 'requests', needs: ['store.customers.read'] },
  { key: 'products', to: '/products', group: 'catalog', needs: ['store.products.read'] },
  { key: 'categories', to: '/categories', group: 'catalog', needs: ['store.products.read'] },
  { key: 'branding', to: '/branding', group: 'store', needs: ['store.settings.read'] },
  { key: 'home', to: '/home', group: 'store', needs: ['store.settings.read'] },
  { key: 'pages', to: '/pages', group: 'store', needs: ['store.settings.read'] },
  { key: 'popups', to: '/popups', group: 'store', needs: ['store.promotions.read'] },
  { key: 'settings', to: '/settings', group: 'store', needs: ['store.settings.read'] },
  { key: 'analytics', to: '/analytics', group: 'store', needs: ['store.settings.read'] },
  { key: 'staff', to: '/staff', group: 'team', needs: ['store.staff.read'] },
  { key: 'activity', to: '/activity', group: 'team', needs: ['store.audit.read'] },
  { key: 'support', to: '/support', group: 'team', needs: ['store.support.use'] },
  { key: 'account', to: '/account', group: 'team', needs: [] },
];

export const NAV_GROUPS: readonly NavGroup[] = ['requests', 'catalog', 'store', 'team'];

export function visibleNav(capabilities: readonly string[]): NavItem[] {
  return NAV_ITEMS.filter((item) => item.needs.every((need) => capabilities.includes(need)));
}

/** Where a member lands after signing in: the first screen their role can use. */
export function homePath(capabilities: readonly string[]): string {
  return visibleNav(capabilities)[0]?.to ?? '/account';
}
